/**
 * src/components/flo/floLayout.js
 *
 * 레인 흐름도 배치 — 재고·공정 이분 그래프의 노드마다 층(열)과 레인 안 세로 위치를 정한다.
 * 순수 함수만 둔다.
 *
 * 좌표는 px 가 아니라 격자다.
 * 층은 열 번호이고, 세로는 레인 안 단위(U) 좌표다.
 * 이 격자 좌표를 칸 폭·단위 높이를 곱해 px 로 바꾸는 일은 floGeometry.js 가 한다.
 * 배치를 한 곳에서 결정형으로 정해야 같은 결과가 늘 같은 그림이 되고, 테스트로 잠글 수 있다.
 *
 * 단계는 넷이다(레이어드 그래프 배치).
 *   층    호 무게를 둔 최장 경로다.
 *         상류 전개는 뿌리가 수요 쪽 끝이라 싱크 정렬, 하류 전개는 소스 정렬이다.
 *         재고↔공정 호는 무게 1, 집약 노드의 재고→재고 호는 무게 2 라 재고는 짝수 층, 공정은 홀수 층에 놓인다.
 *   더미  두 층 이상을 건너는 호는 사이 층마다 도착 레인에 1U 슬롯을 둔다.
 *         슬롯은 칸 쌓기의 한 항목이라 노드와도, 다른 호의 슬롯과도 자리가 겹칠 수 없다.
 *   순서  같은 레인·같은 층(칸) 안의 순서만 바꾼다. 레인은 고정이다.
 *         이웃의 전역 세로 위치 평균으로 줄 세우기를 뿌리 쪽부터 왕복하고, 교차 수가 가장 적은 순서를 채택한다.
 *   세로  먼저 먼 쪽 끝부터 하위 흐름이 층별로 차지하는 세로 범위(윤곽)를 모아 형제 사이 간격을 정한다.
 *         그다음 뿌리 쪽 칸부터 한 방향으로, 칸 안 순서를 지키며 같은 레인 이웃과 높이를 맞춰 곧게 편다.
 *         한 노드로 모이는 형제는 그 노드의 선을 가운데로 두고 위아래로 벌어진다.
 *         벌어지는 폭은 형제 뒤로 이어지는 흐름의 폭을 따르므로, 한 열 안의 간격이 고르지 않을 수 있다.
 *         간격을 고르게 두는 것보다 1:1 사슬을 곧게, 모이는 선을 대칭으로 두는 것이 먼저다.
 *         그러느라 위쪽 자리가 모자라면 레인이 그만큼 높아진다.
 *
 * 순환(왕복 lane)의 되돌이 호는 층과 순서 계산에서 빼고, 도착 레인 바닥에 예약한 채널 행을 따라 돌아간다.
 * 시작 순서와 정렬 키를 전부 고정한다 — 입력 순서가 흔들려도 같은 그림이 되어야 블록 id 가 같다.
 */

// 칸 순서 왕복 횟수. 흐름도 규모(노드 150 이하)에서 더 돌려도 교차가 거의 줄지 않는다.
const SWEEPS = 6;
// 더미 슬롯 높이(U)와 그 안의 선 위치.
export const DUMMY_H = 1.0;
export const DUMMY_ANCHOR = 0.5;
// 되돌이 채널 한 줄의 높이(U).
export const CHANNEL_H = 1.0;
// 원하는 위치가 없는 항목의 등조 회귀 가중치 —
// 이웃에 붙어 쌓이기만 하고 다른 항목을 끌지 않게 아주 작게 둔다.
const FILL_WEIGHT = 1e-6;

/** 등조 회귀 덩어리 [가중 합, 가중치, 항목 수] 의 가중 평균. */
function mean(block) {
  return block[0] / block[1];
}

/** 문자열 비교 — localeCompare 를 쓰지 않고 코드 유닛 순서로만 가른다. */
function cmpStr(a, b) {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/** 배열 사전식(lexicographic) 비교 — 요소가 배열이면 재귀로, 아니면 값으로 가른다. */
function cmpArrLex(a, b) {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i += 1) {
    const x = a[i];
    const y = b[i];
    let c;
    if (Array.isArray(x) && Array.isArray(y)) c = cmpArrLex(x, y);
    else if (typeof x === "number" && typeof y === "number") c = x - y;
    else c = cmpStr(x, y);
    if (c) return c;
  }
  return a.length - b.length;
}

/** laneRank(객체 또는 Map)에서 레인 하나의 정렬 키 배열을 읽는다 — 없으면 빈 배열이다. */
function laneRankOf(laneRank, lane) {
  if (!laneRank) return [];
  const v = laneRank instanceof Map ? laneRank.get(lane) : laneRank[lane];
  return Array.isArray(v) ? v : [];
}

/** 역전 수(병합 정렬) — 교차 수 세기에 쓴다. */
export function countInversions(seq) {
  if (seq.length < 2) return 0;
  const mid = Math.floor(seq.length / 2);
  const left = seq.slice(0, mid);
  const right = seq.slice(mid);
  let count = countInversions(left) + countInversions(right);
  left.sort((a, b) => a - b);
  right.sort((a, b) => a - b);
  let j = 0;
  for (let i = 0; i < left.length; i += 1) {
    const v = left[i];
    while (j < right.length && right[j] < v) j += 1;
    count += j;
  }
  return count;
}

/**
 * 순환을 만드는 엣지 번호. 탐색 방향(뿌리에서 멀어지는 쪽)으로 깊이 우선 탐색한다.
 * 상류 전개의 탐색 방향은 물리 흐름의 반대다.
 * 시작점은 뿌리를 먼저, 나머지는 order 순서로 둔다.
 *
 * order: string[], edges: [string, string][], roots: string[], walkUp: boolean.
 * 반환은 되돌이로 잡힌 엣지 번호의 Set 이다.
 */
export function backEdges(order, edges, roots, walkUp) {
  const outIdx = new Map();
  for (let i = 0; i < edges.length; i += 1) {
    const [a, b] = edges[i];
    const key = walkUp ? b : a;
    if (!outIdx.has(key)) outIdx.set(key, []);
    outIdx.get(key).push(i);
  }
  const state = new Map(); // 1 = 탐색 중, 2 = 끝
  const back = new Set();
  const inOrder = new Set(order);
  const rootSet = new Set(roots);
  const starts = [
    ...roots.filter((r) => inOrder.has(r)),
    ...order.filter((k) => !rootSet.has(k)),
  ];
  for (let s = 0; s < starts.length; s += 1) {
    const start = starts[s];
    if (state.has(start)) continue;
    state.set(start, 1);
    const stack = [{ node: start, list: outIdx.get(start) || [], pos: 0 }];
    while (stack.length) {
      const frame = stack[stack.length - 1];
      if (frame.pos >= frame.list.length) {
        state.set(frame.node, 2);
        stack.pop();
        continue;
      }
      const i = frame.list[frame.pos];
      frame.pos += 1;
      const [a, b] = edges[i];
      const nxt = walkUp ? a : b;
      if (state.get(nxt) === 1) {
        back.add(i);
      } else if (!state.has(nxt)) {
        state.set(nxt, 1);
        stack.push({ node: nxt, list: outIdx.get(nxt) || [], pos: 0 });
      }
    }
  }
  return back;
}

/**
 * 층 번호(Map) — 무게를 둔 최장 경로다.
 * 상류(walkUp)는 싱크(수요 쪽 끝) 정렬, 하류는 소스(공급 쪽 끝) 정렬이다.
 *
 * order: string[], arcs: {s,t,w}[], back: Set<number>, walkUp: boolean.
 */
function assignLayersCore(order, arcs, back, walkUp) {
  const fwd = new Map();
  const rev = new Map();
  const indeg = new Map(order.map((k) => [k, 0]));
  for (let i = 0; i < arcs.length; i += 1) {
    if (back.has(i)) continue;
    const a = arcs[i];
    const w = a.w == null ? 1 : a.w; // Arc 기본 가중치 1 — 호출부가 정규화하지 않아도 여기서 보장한다
    if (!fwd.has(a.s)) fwd.set(a.s, []);
    fwd.get(a.s).push([a.t, w]);
    if (!rev.has(a.t)) rev.set(a.t, []);
    rev.get(a.t).push([a.s, w]);
    indeg.set(a.t, (indeg.get(a.t) || 0) + 1);
  }
  const topo = [];
  const q = order.filter((k) => indeg.get(k) === 0);
  let qi = 0;
  while (qi < q.length) {
    const k = q[qi];
    qi += 1;
    topo.push(k);
    const nexts = fwd.get(k) || [];
    for (let i = 0; i < nexts.length; i += 1) {
      const m = nexts[i][0];
      indeg.set(m, indeg.get(m) - 1);
      if (indeg.get(m) === 0) q.push(m);
    }
  }
  const result = new Map();
  if (walkUp) {
    const dist = new Map();
    for (let i = topo.length - 1; i >= 0; i -= 1) {
      const k = topo[i];
      let best = 0;
      const nexts = fwd.get(k) || [];
      for (let n = 0; n < nexts.length; n += 1) {
        const [m, w] = nexts[n];
        if (dist.has(m)) best = Math.max(best, w + dist.get(m));
      }
      dist.set(k, best);
    }
    let top = 0;
    dist.forEach((v) => { top = Math.max(top, v); });
    order.forEach((k) => result.set(k, top - (dist.has(k) ? dist.get(k) : 0)));
    return result;
  }
  const lay = new Map();
  for (let i = 0; i < topo.length; i += 1) {
    const k = topo[i];
    let best = 0;
    const prevs = rev.get(k) || [];
    for (let n = 0; n < prevs.length; n += 1) {
      const [m, w] = prevs[n];
      if (lay.has(m)) best = Math.max(best, lay.get(m) + w);
    }
    lay.set(k, best);
  }
  order.forEach((k) => result.set(k, lay.has(k) ? lay.get(k) : 0));
  return result;
}

/** assignLayersCore 를 일반 객체로 감싼 공개판 — 단독 테스트용이다. */
export function assignLayers(order, arcs, back, walkUp) {
  return Object.fromEntries(assignLayersCore(order, arcs, back, walkUp));
}

/**
 * 레인 순서 — 거점 유형 순위, 가장 왼쪽 층, 평균 층, 레인 정렬 키 순이다.
 * 유형을 먼저 봐야 공급사 -> 생산 -> 물류가 위에서 아래로 묶인다.
 *
 * order: string[], laneOf: Map<string,string>, laneRank: object|Map<string,number[]>,
 * layer: Map<string,number>.
 */
export function laneOrder(order, laneOf, laneRank, layer) {
  const by = new Map();
  for (let i = 0; i < order.length; i += 1) {
    const k = order[i];
    const ln = laneOf.get(k);
    if (!by.has(ln)) by.set(ln, []);
    by.get(ln).push(layer.get(k));
  }
  const keyOf = (ln) => {
    const raw = laneRankOf(laneRank, ln);
    const full = raw.length ? raw : [9, ln];
    const layers = by.get(ln);
    let minL = layers[0];
    let sum = 0;
    for (let i = 0; i < layers.length; i += 1) {
      if (layers[i] < minL) minL = layers[i];
      sum += layers[i];
    }
    return { typeRank: full[0], minL, avg: sum / layers.length, full };
  };
  return [...by.keys()].sort((a, b) => {
    const ka = keyOf(a);
    const kb = keyOf(b);
    return (ka.typeRank - kb.typeRank) || (ka.minL - kb.minL) || (ka.avg - kb.avg)
      || cmpArrLex(ka.full, kb.full);
  });
}

/**
 * 배치 전체. arcs 는 물리 흐름 방향이고 중복이 없어야 한다.
 * back 을 주면(배열 또는 Set) 그 호들을 되돌이로 쓰고, 주지 않으면 자동으로 판정한다.
 *
 * nodes: {key,lane,h,anchor,tie}[], arcs: {s,t,w}[], roots: string[], walkUp: boolean,
 * laneRank: object|Map<string,number[]>, back: number[]|Set<number>|undefined.
 */
export function layoutGraph({
  nodes = [], arcs = [], roots = [], walkUp = false, laneRank = {}, back,
} = {}) {
  const arcsN = arcs.map((a) => ({ s: a.s, t: a.t, w: a.w == null ? 1 : a.w }));
  // 정렬 키 순으로 걷는다 — 순환에서 어느 호를 되돌이로 볼지까지 입력 순서와 무관해야 한다.
  const order = nodes
    .slice()
    .sort((a, b) => cmpStr(a.tie, b.tie) || cmpStr(a.key, b.key))
    .map((n) => n.key);

  // 되돌이 = 호출자가 표시한 호 + 남은 호에서 자동으로 찾은 호.
  // 표시가 순환을 다 끊지 못해도 층 계산이 막히지 않는다 — 남은 순환의 노드가 한 층으로 뭉개지지 않는다.
  const forced = new Set(back || []);
  const rest = [];
  arcsN.forEach((_, i) => { if (!forced.has(i)) rest.push(i); });
  const found = backEdges(order, rest.map((i) => [arcsN[i].s, arcsN[i].t]), roots.slice().sort(cmpStr), walkUp);
  const backSet = new Set([...forced, ...[...found].map((j) => rest[j])]);

  const layer = assignLayersCore(order, arcsN, backSet, walkUp);

  const laneOf = new Map(nodes.map((n) => [n.key, n.lane]));
  const lanes = laneOrder(order, laneOf, laneRank, layer);
  const laneIdx = new Map(lanes.map((ln, i) => [ln, i]));

  // 항목 = 노드 + 더미. 사슬 = 호마다 [출발, 더미…, 도착].
  const hOf = new Map(nodes.map((n) => [n.key, n.h]));
  const anchorOf = new Map(nodes.map((n) => [n.key, n.anchor]));
  const tieOf = new Map(nodes.map((n) => [n.key, `0|${n.tie}`]));
  const lay = new Map(layer);
  const chains = new Map(); // arcIndex -> [출발, 더미…, 도착]
  for (let i = 0; i < arcsN.length; i += 1) {
    if (backSet.has(i)) continue;
    const a = arcsN[i];
    const chain = [a.s];
    const sLayer = lay.get(a.s);
    const tLayer = lay.get(a.t);
    for (let c = sLayer + 1; c < tLayer; c += 1) {
      const d = `~${i}:${c}`;
      hOf.set(d, DUMMY_H);
      anchorOf.set(d, DUMMY_ANCHOR);
      laneOf.set(d, laneOf.get(a.t));
      lay.set(d, c);
      tieOf.set(d, `1|${tieOf.get(a.t)}|${String(i).padStart(6, "0")}`);
      chain.push(d);
    }
    chain.push(a.t);
    chains.set(i, chain);
  }

  const leftMap = new Map();
  const rightMap = new Map();
  chains.forEach((chain) => {
    for (let i = 0; i < chain.length - 1; i += 1) {
      const u = chain[i];
      const v = chain[i + 1];
      if (!rightMap.has(u)) rightMap.set(u, []);
      rightMap.get(u).push(v);
      if (!leftMap.has(v)) leftMap.set(v, []);
      leftMap.get(v).push(u);
    }
  });

  // 칸 = (레인, 층). cellMeta 는 칸 키가 고정된 뒤로는 값(순서)만 바뀐다.
  const cellKeyOf = (lane, ly) => `${lane}\u0001${ly}`;
  const allKeys = [...lay.keys()].sort((a, b) => cmpStr(tieOf.get(a), tieOf.get(b)));
  const cellMeta = new Map(); // cellKey -> {lane, layer}
  const cells = new Map(); // cellKey -> string[]
  for (let i = 0; i < allKeys.length; i += 1) {
    const k = allKeys[i];
    const lane = laneOf.get(k);
    const ly = lay.get(k);
    const ck = cellKeyOf(lane, ly);
    if (!cells.has(ck)) {
      cells.set(ck, []);
      cellMeta.set(ck, { lane, layer: ly });
    }
    cells.get(ck).push(k);
  }
  const cellKeysList = [...cellMeta.entries()].map(([ck, meta]) => ({
    key: ck, lane: meta.lane, layer: meta.layer,
  }));
  cellKeysList.sort((a, b) => (a.layer - b.layer) || (laneIdx.get(a.lane) - laneIdx.get(b.lane)));
  const layersSorted = [...new Set(cellKeysList.map((c) => c.layer))].sort((a, b) => a - b);
  const cellKeysByLayer = new Map();
  cellKeysList.forEach((ck) => {
    if (!cellKeysByLayer.has(ck.layer)) cellKeysByLayer.set(ck.layer, []);
    cellKeysByLayer.get(ck.layer).push(ck.key);
  });

  /** 레인 안 누적 쌓기로 모든 항목의 전역 세로 위치(엣지 선)를 구한다. */
  function positions() {
    const laneH = new Map();
    cellMeta.forEach((meta, ck) => {
      const ks = cells.get(ck);
      let sum = 0;
      for (let i = 0; i < ks.length; i += 1) sum += hOf.get(ks[i]);
      const cur = laneH.get(meta.lane) || 0;
      if (sum > cur) laneH.set(meta.lane, sum);
    });
    const top = new Map();
    let acc = 0;
    for (let i = 0; i < lanes.length; i += 1) {
      const ln = lanes[i];
      top.set(ln, acc);
      acc += laneH.get(ln) || 0;
    }
    const pos = new Map();
    cellMeta.forEach((meta, ck) => {
      let y = top.get(meta.lane);
      const ks = cells.get(ck);
      for (let i = 0; i < ks.length; i += 1) {
        const k = ks[i];
        pos.set(k, y + anchorOf.get(k));
        y += hOf.get(k);
      }
    });
    return pos;
  }

  /** 현재 칸 순서에서의 교차 수 — 층별로 (출발 위치, 도착 위치) 쌍을 모아 역전 수를 센다. */
  function crossings() {
    const pos = positions();
    const byLayer = new Map();
    rightMap.forEach((vs, u) => {
      const lu = lay.get(u);
      if (!byLayer.has(lu)) byLayer.set(lu, []);
      const arr = byLayer.get(lu);
      for (let i = 0; i < vs.length; i += 1) arr.push([pos.get(u), pos.get(vs[i])]);
    });
    let total = 0;
    byLayer.forEach((segs) => {
      segs.sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));
      total += countInversions(segs.map((s) => s[1]));
    });
    return total;
  }

  let best = crossings();
  let bestCells = new Map([...cells].map(([k, v]) => [k, v.slice()]));
  for (let i = 0; i < SWEEPS; i += 1) {
    const fromRoot = i % 2 === 0;
    const rightward = fromRoot !== walkUp;
    const seq = rightward ? layersSorted : layersSorted.slice().reverse();
    const nbr = rightward ? leftMap : rightMap;
    for (let s = 0; s < seq.length; s += 1) {
      const c = seq[s];
      const pos = positions();
      const cks = cellKeysByLayer.get(c) || [];
      for (let ci = 0; ci < cks.length; ci += 1) {
        const ck = cks[ci];
        const ks = cells.get(ck);
        const idxOf = new Map(ks.map((k, n) => [k, n]));
        const sortKey = (k) => {
          const neighbors = nbr.get(k) || [];
          let val;
          if (neighbors.length) {
            let sum = 0;
            for (let n = 0; n < neighbors.length; n += 1) sum += pos.get(neighbors[n]);
            val = sum / neighbors.length;
          } else {
            val = pos.get(k);
          }
          return [val, idxOf.get(k), tieOf.get(k)];
        };
        const sorted = ks.slice().sort((a, b) => {
          const ka = sortKey(a);
          const kb = sortKey(b);
          return (ka[0] - kb[0]) || (ka[1] - kb[1]) || cmpStr(ka[2], kb[2]);
        });
        cells.set(ck, sorted);
      }
    }
    const cr = crossings();
    if (cr < best) {
      best = cr;
      bestCells = new Map([...cells].map(([k, v]) => [k, v.slice()]));
    }
  }
  cells.clear();
  bestCells.forEach((v, k) => cells.set(k, v));

  // 곧게 펴기 — 뿌리 쪽 칸부터 한 방향으로, 칸 안 순서를 지키며 같은 레인 이웃의 선 높이에 맞춘다.
  const topY = new Map();

  /**
   * 칸 하나를 순서대로 쌓되, 원하는 위치와의 제곱 오차 합이 가장 작은 자리에 둔다(등조 회귀, PAVA).
   * 원하는 위치가 없는 항목은 바로 앞 항목에 붙여 쌓고, 앞이 없으면 뒤 항목에 붙인다.
   */
  function place(ck, want) {
    const ks = cells.get(ck);
    const off = [];
    let acc = 0;
    for (let i = 0; i < ks.length; i += 1) {
      off.push(acc);
      acc += hOf.get(ks[i]);
    }
    const zs = ks.map((k, n) => {
      const wv = want.has(k) ? want.get(k) : null;
      return (wv === null || wv === undefined) ? null : wv - off[n];
    });
    const known = zs.filter((z) => z !== null);
    let prev = known.length ? known[0] : 0.0;
    const filled = [];
    const weight = [];
    for (let i = 0; i < zs.length; i += 1) {
      const z = zs[i];
      if (z === null) {
        filled.push(prev);
        weight.push(FILL_WEIGHT);
      } else {
        filled.push(z);
        weight.push(1.0);
        prev = z;
      }
    }
    const blocks = []; // [가중 합, 가중치, 항목 수]
    for (let i = 0; i < filled.length; i += 1) {
      const z = filled[i];
      const wt = weight[i];
      blocks.push([z * wt, wt, 1]);
      while (blocks.length >= 2 && mean(blocks[blocks.length - 2]) > mean(blocks[blocks.length - 1])) {
        const b = blocks.pop();
        const prevBlock = blocks[blocks.length - 1];
        prevBlock[0] += b[0];
        prevBlock[1] += b[1];
        prevBlock[2] += b[2];
      }
    }
    let n = 0;
    for (let bi = 0; bi < blocks.length; bi += 1) {
      const [total, wt, cnt] = blocks[bi];
      for (let c = 0; c < cnt; c += 1) {
        topY.set(ks[n], total / wt + off[n]);
        n += 1;
      }
    }
  }

  /** 같은 레인 이웃의 전역 세로 위치 중앙값 — 없으면 null. */
  function desired(k, nbr) {
    const neighbors = nbr.get(k) || [];
    const ys = [];
    for (let i = 0; i < neighbors.length; i += 1) {
      const m = neighbors[i];
      if (topY.has(m) && laneOf.get(m) === laneOf.get(k)) ys.push(topY.get(m) + anchorOf.get(m));
    }
    if (!ys.length) return null;
    ys.sort((a, b) => a - b);
    const mid = Math.floor(ys.length / 2);
    const line = ys.length % 2 ? ys[mid] : (ys[mid - 1] + ys[mid]) / 2;
    return line - anchorOf.get(k);
  }

  const rootFirst = walkUp ? layersSorted.slice().reverse() : layersSorted;
  const rootFirstRev = rootFirst.slice().reverse();
  const rootSide = walkUp ? rightMap : leftMap;
  const farSide = walkUp ? leftMap : rightMap;

  // 하위 흐름 윤곽 — 형제 사이 간격을 형제 자신의 키가 아니라 그 뒤로 이어지는 흐름 전체의 폭으로 정한다.
  // 뿌리 쪽부터 제 키만큼만 붙여 쌓으면, 먼 쪽 열에서 키 큰 노드끼리 밀려 1:1 사슬이 꺾이고 모이는 선이 비대칭이 된다.
  // 그래서 먼 쪽 끝부터 항목마다 자기와 하위 흐름이 층별로 차지하는 세로 범위를 자기 선 기준 상대값으로 모은다.
  // 하위 흐름은 같은 레인에서 이 항목을 유일한 뿌리 쪽 이웃으로 둔 먼 쪽 이웃(자식)으로 잇는다.
  // 형제끼리는 층마다 범위가 겹치지 않는 최소 간격만큼 떼고, 부모 선은 자식 선의 중앙값에 둔다.
  // 그래서 1:1 이면 부모와 자식의 선이 같고, 둘로 모이면 부모가 두 선의 한가운데다.
  // 칸 안 순서가 두 형제의 하위 흐름을 엇갈려 끼운 층은 간격 계산에서 뺀다.
  // "한쪽이 통째로 위"라는 전제가 그 층에서는 틀려, 넣으면 필요 없이 벌어진다.
  // 그런 층은 칸 쌓기와 뒤의 부모 다시 가운데 두기가 맡는다.
  const idxInCell = new Map();
  const prefH = new Map(); // 칸 -> 앞 항목들 키의 누적 합(두 순번 사이에 끼인 키를 바로 구한다)
  cells.forEach((ks, ck) => {
    const pre = [0];
    ks.forEach((k, n) => {
      idxInCell.set(k, n);
      pre.push(pre[n] + hOf.get(k));
    });
    prefH.set(ck, pre);
  });
  const parentOf = new Map();
  lay.forEach((_, k) => {
    const up = sameLane(rootSide, k);
    if (up.length === 1) parentOf.set(k, up[0]);
  });
  function kidsOf(k) {
    const kids = [...new Set(sameLane(farSide, k))].filter((m) => parentOf.get(m) === k);
    return kids.sort((a, b) => idxInCell.get(a) - idxInCell.get(b));
  }
  // 윤곽 한 층 = [위, 아래, 칸 안 첫 순번, 끝 순번].
  function unionInto(dst, src, shift) {
    src.forEach(([lo, hi, i0, i1], ly) => {
      const cur = dst.get(ly);
      dst.set(ly, cur
        ? [Math.min(cur[0], lo + shift), Math.max(cur[1], hi + shift), Math.min(cur[2], i0), Math.max(cur[3], i1)]
        : [lo + shift, hi + shift, i0, i1]);
    });
  }
  const contour = new Map(); // 항목 -> Map(층 -> 윤곽 한 층), 자기 선을 0 으로 둔 상대값
  const relOff = new Map(); // 자식 -> 부모 선에서 자기 선까지의 상대 위치
  for (let s = 0; s < rootFirstRev.length; s += 1) {
    const c = rootFirstRev[s];
    const cks = cellKeysByLayer.get(c) || [];
    for (let ci = 0; ci < cks.length; ci += 1) {
      const ks = cells.get(cks[ci]);
      for (let i = 0; i < ks.length; i += 1) {
        const k = ks[i];
        const idx = idxInCell.get(k);
        const own = new Map([[c, [-anchorOf.get(k), hOf.get(k) - anchorOf.get(k), idx, idx]]]);
        const kids = kidsOf(k);
        if (kids.length) {
          const offs = [0];
          const acc = new Map();
          unionInto(acc, contour.get(kids[0]), 0);
          const lane = laneOf.get(k);
          for (let n = 1; n < kids.length; n += 1) {
            const cont = contour.get(kids[n]);
            let d = -Infinity;
            cont.forEach(([lo, , i0], ly) => {
              const a = acc.get(ly);
              if (!a || a[3] >= i0) return;
              // 두 흐름 사이에 끼인 다른 항목의 키만큼도 자리를 비워 둔다.
              const pre = prefH.get(cellKeyOf(lane, ly));
              d = Math.max(d, a[1] - lo + (pre[i0] - pre[a[3] + 1]));
            });
            offs.push(d);
            unionInto(acc, cont, d);
          }
          const mid = median(offs);
          kids.forEach((kid, n) => relOff.set(kid, offs[n] - mid));
          unionInto(own, acc, -mid);
        }
        contour.set(k, own);
      }
    }
  }

  // 뿌리 쪽 배치 — 자식은 부모 선에 윤곽이 정한 상대 위치를 더한 자리를 원한다.
  // 뿌리 쪽 이웃이 여럿인 항목(여러 곳에 쓰이는 부품 등)은 그 이웃들 선의 중앙값을 원한다.
  const had = new Map();
  for (let s = 0; s < rootFirst.length; s += 1) {
    const c = rootFirst[s];
    const cks = cellKeysByLayer.get(c) || [];
    for (let ci = 0; ci < cks.length; ci += 1) {
      const ck = cks[ci];
      const ks = cells.get(ck);
      const want = new Map();
      for (let i = 0; i < ks.length; i += 1) {
        const k = ks[i];
        const p = parentOf.get(k);
        want.set(k, p !== undefined && topY.has(p)
          ? line(p) + relOff.get(k) - anchorOf.get(k)
          : desired(k, rootSide));
      }
      want.forEach((w, k) => had.set(k, w !== null));
      place(ck, want);
    }
  }
  // 뿌리 쪽 이웃이 없는 노드(다른 레인에서만 이어지는 노드 등)는 먼 쪽 이웃에 맞춘다.
  for (let s = 0; s < rootFirstRev.length; s += 1) {
    const c = rootFirstRev[s];
    const cks = cellKeysByLayer.get(c) || [];
    for (let ci = 0; ci < cks.length; ci += 1) {
      const ck = cks[ci];
      const ks = cells.get(ck);
      if (ks.every((k) => had.get(k))) continue;
      const want = new Map();
      for (let i = 0; i < ks.length; i += 1) {
        const k = ks[i];
        want.set(k, had.get(k) ? topY.get(k) : desired(k, farSide));
      }
      place(ck, want);
    }
  }

  function sameLane(nbr, k) {
    const neighbors = nbr.get(k) || [];
    return neighbors.filter((m) => laneOf.get(m) === laneOf.get(k));
  }
  function line(k) {
    return topY.get(k) + anchorOf.get(k);
  }
  function median(vals) {
    const v = vals.slice().sort((a, b) => a - b);
    const mid = Math.floor(v.length / 2);
    return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
  }

  // 부모 다시 가운데 두기 — 먼 쪽에서 뿌리 쪽으로 훑어, 자식이 있는 항목을 자식이 실제로 놓인 선의 중앙값에 둔다.
  // 칸 안 순서가 두 부모의 자식을 엇갈려 끼우면(다른 거점에서 오는 운송이 사이에 끼는 경우 등)
  // 윤곽이 정한 자리에 자식이 다 앉지 못하고 밀린다.
  // 그때도 부모가 밀린 자식들의 가운데로 옮기면 1:1 은 곧고 모이는 선은 대칭으로 남는다.
  // 먼 쪽부터 훑으므로 자식 쪽 위치가 먼저 확정되고, 부모가 옮긴 결과가 다시 그 부모의 부모에게 이어진다.
  for (let s = 0; s < rootFirstRev.length; s += 1) {
    const c = rootFirstRev[s];
    const cks = cellKeysByLayer.get(c) || [];
    for (let ci = 0; ci < cks.length; ci += 1) {
      const ck = cks[ci];
      const ks = cells.get(ck);
      const want = new Map();
      for (let i = 0; i < ks.length; i += 1) {
        const k = ks[i];
        const kids = kidsOf(k);
        want.set(k, kids.length ? median(kids.map(line)) - anchorOf.get(k) : topY.get(k));
      }
      place(ck, want);
    }
  }

  // 레인 정규화 — 맨 위 항목이 0 이 되게 내리고, 레인 높이 = 내용 높이 + 되돌이 채널.
  const laneLo = new Map();
  const laneHi = new Map();
  topY.forEach((y, k) => {
    const ln = laneOf.get(k);
    laneLo.set(ln, laneLo.has(ln) ? Math.min(laneLo.get(ln), y) : y);
    const hi = y + hOf.get(k);
    laneHi.set(ln, laneHi.has(ln) ? Math.max(laneHi.get(ln), hi) : hi);
  });
  topY.forEach((y, k) => { topY.set(k, y - laneLo.get(laneOf.get(k))); });
  const laneH = new Map();
  lanes.forEach((ln) => laneH.set(ln, (laneHi.get(ln) || 0.0) - (laneLo.get(ln) || 0.0)));
  const channels = new Map();
  const backSorted = [...backSet].sort((a, b) => a - b);
  backSorted.forEach((i) => {
    const ln = laneOf.get(arcsN[i].t);
    channels.set(i, laneH.get(ln) + CHANNEL_H / 2);
    laneH.set(ln, laneH.get(ln) + CHANNEL_H);
  });

  // 포트 순번 — 한 노드의 같은 쪽 호를 이웃의 전역 높이 순으로 줄 세운다.
  const laneTop = new Map();
  {
    let acc = 0;
    for (let i = 0; i < lanes.length; i += 1) {
      laneTop.set(lanes[i], acc);
      acc += laneH.get(lanes[i]);
    }
  }
  function absLine(k) {
    return laneTop.get(laneOf.get(k)) + topY.get(k) + anchorOf.get(k);
  }
  function cmpPortKey(a, b) {
    return (a[0] - b[0]) || (a[1] - b[1]) || cmpStr(a[2], b[2]);
  }

  const outs = new Map(); // node -> [[lane_idx, y, tie], arcIndex][]
  const ins = new Map();
  for (let i = 0; i < arcsN.length; i += 1) {
    const a = arcsN[i];
    let nxt;
    let prv;
    if (backSet.has(i)) {
      const ch = laneTop.get(laneOf.get(a.t)) + channels.get(i);
      nxt = [laneIdx.get(laneOf.get(a.t)), ch, tieOf.get(a.t)];
      prv = [laneIdx.get(laneOf.get(a.s)), ch, tieOf.get(a.s)];
    } else {
      const chain = chains.get(i);
      const c1 = chain[1];
      const cm2 = chain[chain.length - 2];
      nxt = [laneIdx.get(laneOf.get(c1)), absLine(c1), tieOf.get(c1)];
      prv = [laneIdx.get(laneOf.get(cm2)), absLine(cm2), tieOf.get(cm2)];
    }
    if (!outs.has(a.s)) outs.set(a.s, []);
    outs.get(a.s).push([nxt, i]);
    if (!ins.has(a.t)) ins.set(a.t, []);
    ins.get(a.t).push([prv, i]);
  }
  const portOut = {};
  const portIn = {};
  const nOut = {};
  const nIn = {};
  outs.forEach((lst, k) => {
    lst.sort((x, y) => cmpPortKey(x[0], y[0]) || (x[1] - y[1]));
    nOut[k] = lst.length;
    lst.forEach(([, i], n) => { portOut[i] = n; });
  });
  ins.forEach((lst, k) => {
    lst.sort((x, y) => cmpPortKey(x[0], y[0]) || (x[1] - y[1]));
    nIn[k] = lst.length;
    lst.forEach(([, i], n) => { portIn[i] = n; });
  });

  const waypoints = {};
  chains.forEach((chain, i) => {
    if (chain.length > 2) {
      waypoints[i] = chain.slice(1, -1).map((d) => [lay.get(d), topY.get(d) + anchorOf.get(d)]);
    }
  });

  const layerOut = {};
  const yOut = {};
  order.forEach((k) => {
    layerOut[k] = layer.get(k);
    yOut[k] = topY.get(k) + anchorOf.get(k);
  });

  return {
    layer: layerOut,
    y: yOut,
    lanes,
    laneH: Object.fromEntries(laneH),
    waypoints,
    portOut,
    portIn,
    nOut,
    nIn,
    channels: Object.fromEntries(channels),
    back: backSorted,
    crossings: best,
  };
}
